import * as s from '~js/utils/selectors';
import * as a from '~js/utils/attributes';
import * as c from '~js/utils/classes';
import {
  throttle,
} from '~js/utils/utils';

export class Tooltip {
  constructor(el) {
    this.tooltip = el;
    if (!this.tooltip.hasAttribute(a.attributes$F.tooltip)) {
      return;
    }

    this.rootClass = c.classes$P.root;
    this.isAnimatingClass = c.classes$P.isAnimating;
    this.label = this.tooltip.getAttribute(a.attributes$F.tooltip);
    this.transitionSpeed = 200;
    this.hideTransitionTimeout = 0;
    this.animatedContainer = this.tooltip.closest(s.selectors$_.aos);
    this.addPinEvent = () => this.addPin();
    this.addPinMouseEvent = () => this.addPin(true);
    this.removePinEvent = (event) => throttle(this.removePin(event), 50);
    this.removePinMouseEvent = (event) => this.removePin(event, true, true);
    this.init();
  }

  init() {
    if (!document.querySelector(s.selectors$_.tooltipContainer)) {
      const tooltipTemplate = `<div class="${this.rootClass}__inner"><div class="${this.rootClass}__arrow" data-tooltip-arrow></div><div class="${this.rootClass}__text label-typography"></div></div>`;
      const tooltipElement = document.createElement('div');
      tooltipElement.className = `${this.rootClass} ${this.isAnimatingClass}`;
      tooltipElement.setAttribute(a.attributes$F.tooltipContainer, '');
      tooltipElement.innerHTML = tooltipTemplate;
      document.body.appendChild(tooltipElement);
    }

    this.tooltip.addEventListener('mouseenter', this.addPinMouseEvent);
    this.tooltip.addEventListener('mouseleave', this.removePinMouseEvent);
    this.tooltip.addEventListener('theme:tooltip:init', this.addPinEvent);
    document.addEventListener('theme:tooltip:close', this.removePinEvent);

    const tooltipTarget = document.querySelector(s.selectors$_.tooltipContainer);

    if (theme.settings.animations && this.animatedContainer) {
      if (this.animatedContainer.getAttribute(a.attributes$F.aos) === 'hero') {
        // Used for PDP and Featured product section
        this.animatedContainer.addEventListener('animationend', () => {
          tooltipTarget.classList.remove(c.classes$P.isAnimating);
        });
      } else {
        this.animatedContainer.addEventListener('transitionend', (event) => {
          // This will fire the event when the last transition end
          if (event.propertyName === 'transform') {
            tooltipTarget.classList.remove(c.classes$P.isAnimating);
          }
        });
      }
    }
  }

  addPin(stopMouseEnter = false) {
    const tooltipTarget = document.querySelector(s.selectors$_.tooltipContainer);
    const tooltipTargetArrow = tooltipTarget.querySelector(s.selectors$_.tooltipArrow);

    if (
      tooltipTarget &&
      ((stopMouseEnter && !this.tooltip.hasAttribute(a.attributes$F.tooltipStopMouseEnter)) || !stopMouseEnter)
    ) {
      const tooltipTargetInner = tooltipTarget.querySelector(`.${this.rootClass}__inner`);
      const tooltipTargetText = tooltipTarget.querySelector(`.${this.rootClass}__text`);
      tooltipTargetText.textContent = this.label;

      const tooltipTargetWidth = tooltipTargetInner.offsetWidth;
      const tooltipRect = this.tooltip.getBoundingClientRect();
      const tooltipTop = tooltipRect.top;
      const tooltipWidth = tooltipRect.width;
      const tooltipHeight = tooltipRect.height;
      const tooltipTargetPositionTop = tooltipTop + tooltipHeight + window.scrollY;
      let tooltipTargetPositionLeft = tooltipRect.left - tooltipTargetWidth / 2 + tooltipWidth / 2;
      let tooltipArrowPositionLeft = '50%';
      const tooltipLeftWithWidth = tooltipTargetPositionLeft + tooltipTargetWidth;
      const tooltipTargetWindowDifference = tooltipLeftWithWidth - window.innerWidth;

      if (tooltipTargetWindowDifference > 0) {
        tooltipTargetPositionLeft -= tooltipTargetWindowDifference;
      }

      if (tooltipTargetPositionLeft < 0) {
        tooltipArrowPositionLeft = `calc(50% + ${tooltipTargetPositionLeft}px)`;
        tooltipTargetPositionLeft = 0;
      }

      tooltipTargetArrow.style.left = tooltipArrowPositionLeft;
      tooltipTarget.style.transform = `translate(${tooltipTargetPositionLeft}px, ${tooltipTargetPositionTop}px)`;

      tooltipTarget.classList.remove(c.classes$P.hiding);
      const onTooltipHiding = (event) => {
        if (event.target !== tooltipTargetInner) return;
        if (event.propertyName === 'transform' || event.propertyName === 'opacity') {
          requestAnimationFrame(() => (tooltipTarget.style.transform = 'translate(0, -100%)'));
        }
        tooltipTarget.removeEventListener('transitionend', onTooltipHiding);
      };
      tooltipTarget.addEventListener('transitionend', onTooltipHiding);

      tooltipTarget.classList.add(c.classes$P.visible);

      document.addEventListener('theme:scroll', this.removePinEvent);
    }
  }

  removePin(event, stopMouseEnter = false, hideTransition = false) {
    const tooltipTarget = document.querySelector(s.selectors$_.tooltipContainer);
    const tooltipVisible = tooltipTarget.classList.contains(c.classes$P.visible);

    if (
      tooltipTarget &&
      ((stopMouseEnter && !this.tooltip.hasAttribute(a.attributes$F.tooltipStopMouseEnter)) || !stopMouseEnter)
    ) {
      if (tooltipVisible && (hideTransition || event.detail.hideTransition)) {
        tooltipTarget.classList.add(c.classes$P.hiding);

        if (this.hideTransitionTimeout) {
          clearTimeout(this.hideTransitionTimeout);
        }

        this.hideTransitionTimeout = setTimeout(() => {
          tooltipTarget.classList.remove(c.classes$P.hiding);
        }, this.transitionSpeed);
      }

      tooltipTarget.classList.remove(c.classes$P.visible);

      document.removeEventListener('theme:scroll', this.removePinEvent);
    }
  }

  unload() {
    this.tooltip.removeEventListener('mouseenter', this.addPinMouseEvent);
    this.tooltip.removeEventListener('mouseleave', this.removePinMouseEvent);
    this.tooltip.removeEventListener('theme:tooltip:init', this.addPinEvent);
    document.removeEventListener('theme:tooltip:close', this.removePinEvent);
    document.removeEventListener('theme:scroll', this.removePinEvent);
  }
}